import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { IonItem, IonRadio, IonRadioGroup } from "@ionic/react";
import { DateTime } from "luxon";
import { SchoolModel } from "model";
import { Asterisk, Button, DataItemView, TimeInput } from "swing-components";

import { TimesTableRowType } from "~components/RequestForm/hooks";
import { translate } from "~utils";

import { SharedModalHeader } from "../SharedModalHeader";

export type LatestAcceptableProps = {
  shouldTrim?: boolean;
  trimTill: DateTime;
};

type ReviewRequestSubmissionProps = {
  onDismiss: () => void;
  onChange: Dispatch<SetStateAction<LatestAcceptableProps>>;
  onSubmit: () => void;
  school: SchoolModel;
  intervals: TimesTableRowType[];
  latestAcceptableValues: LatestAcceptableProps;
};

const findEarliestTrimTill = (currentDay: TimesTableRowType) => {
  const idealStart = currentDay.startTime.plus({ hours: 1 });
  const latestAllowed = currentDay.endTime.minus({ hours: 1 });

  return idealStart <= latestAllowed ? idealStart : latestAllowed;
};

export function ReviewRequestSubmission(props: ReviewRequestSubmissionProps) {
  const { onDismiss, school, latestAcceptableValues, intervals, onChange, onSubmit } = props;

  // FIXME: For now, we only expect to ever need to show this on LMR, SD reqs
  // so this is good enough. Consider refactoring to be like the one in
  // RequestDetailsView if this ever changes.
  const currentDay = intervals.filter((interval) => interval.isEnabled)[0];

  const [showErr, setShowErr] = useState(false);

  useEffect(() => {
    const current = intervals.filter((interval) => interval.isEnabled)[0];

    onChange((old) => ({ shouldTrim: old.shouldTrim, trimTill: findEarliestTrimTill(current) }));
  }, [intervals, onChange]);

  const setTrimTill = (newTrimTill: DateTime) => {
    if (
      newTrimTill > currentDay.endTime.minus({ hours: 1 }) ||
      newTrimTill < currentDay.startTime
    ) {
      setShowErr(true);
      onChange({
        shouldTrim: latestAcceptableValues.shouldTrim,
        trimTill: findEarliestTrimTill(currentDay),
      });
    } else {
      setShowErr(false);
      onChange({
        shouldTrim: latestAcceptableValues.shouldTrim,
        trimTill: newTrimTill,
      });
    }
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <SharedModalHeader title={translate("REQUEST_REVIEW_TITLE")} onDismiss={() => onDismiss()} />
      <div
        css={{
          padding: "var(--ion-safe-area-top) 16px 16px 16px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <div css={{ flex: "1 1 0%", display: "flex", flexDirection: "column", gap: 16 }}>
          <DataItemView icon="School" stackLayout label={school.name} />

          <DataItemView
            icon="Calendar"
            stackLayout
            label={currentDay.dateString.toFormat("ccc, LLL dd")}
          >
            <span css={{ fontSize: 14 }}>
              {currentDay.startTime.toFormat("h:mm a")} - {currentDay.endTime.toFormat("h:mm a")}
            </span>
          </DataItemView>

          <IonRadioGroup
            css={{ display: "flex", flexDirection: "column" }}
            onIonChange={(e) => {
              onChange((old) => ({
                shouldTrim: e?.detail?.value === "yes" ? true : false,
                trimTill: old.trimTill,
              }));
            }}
          >
            <div css={{ fontWeight: "bold", display: "flex" }}>
              <span>{translate("REQUEST_REVIEW_KEEP_OPEN")}</span> <Asterisk />
            </div>

            <IonItem>
              <IonRadio value="yes" labelPlacement="end" justify="start">
                {translate("LABEL_YES")}
              </IonRadio>
            </IonItem>
            <IonItem>
              <IonRadio value="no" labelPlacement="end" justify="start">
                {translate("LABEL_NO")}
              </IonRadio>
            </IonItem>
          </IonRadioGroup>
          {latestAcceptableValues.shouldTrim && (
            <div>
              <div css={{ display: "flex", justifyContent: "space-between" }}>
                <span css={{ fontWeight: "bold" }}>{translate("REQUEST_REVIEW_LATEST_Q")}</span>
                <TimeInput
                  showTextInputOnDesktop
                  value={latestAcceptableValues.trimTill}
                  onChange={setTrimTill}
                />
              </div>
              {showErr && (
                <span css={{ fontSize: 14, color: "red" }}>
                  {translate("REQUEST_REVIEW_ERROR_MESSAGE")}
                </span>
              )}
            </div>
          )}
        </div>
        <div css={{ display: "flex", gap: 16, flexDirection: "column" }}>
          <Button
            onClick={onSubmit}
            isFluid
            status={latestAcceptableValues.shouldTrim !== undefined ? "enabled" : "disabled"}
          >
            {translate("LABEL_REQUEST_SUB")}
          </Button>
          <Button onClick={() => onDismiss()} type="secondary" isFluid>
            {translate("EDIT_REQUEST_HEADER")}
          </Button>
        </div>
      </div>
    </div>
  );
}
